import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface ModuleJobState {
  /** Maps moduleId to the background job id generating rubrics for that module */
  moduleJobs: Record<string, string>;
  /** Maps `${moduleId}:${version}` to an active working-mode apply job id */
  workingModeApplyJobs: Record<string, string>;
  /** Maps `${moduleId}:${version}:${taskId}` to an oral-exam pool generation job */
  oralPoolJobs: Record<string, string>;
  setModuleJob: (moduleId: string, jobId: string) => void;
  clearModuleJob: (moduleId: string) => void;
  setWorkingModeApplyJob: (moduleId: string, version: string, jobId: string) => void;
  clearWorkingModeApplyJob: (moduleId: string, version: string) => void;
  setOralPoolJob: (moduleId: string, version: string, taskId: string, jobId: string) => void;
  clearOralPoolJob: (moduleId: string, version: string, taskId: string) => void;
}

export const workingModeApplyJobKey = (moduleId: string, version: string) => (
  `${moduleId}:${version}`
);

export const oralPoolJobKey = (moduleId: string, version: string, taskId: string) => (
  `${moduleId}:${version}:${taskId}`
);

/**
 * Tracks which module has an active rubric-generation, working-mode apply,
 * or oral-exam pool job so the editor can survive navigation across screens.
 *
 * Server job queue remains the source of truth after full reload; this store is
 * the fast path for in-session remounts before the jobs poll returns.
 */
export const useModuleJobStore = create<ModuleJobState>()(
  persist(
    (set) => ({
      moduleJobs: {},
      workingModeApplyJobs: {},
      oralPoolJobs: {},
      setModuleJob: (moduleId, jobId) => set((state) => ({
        moduleJobs: { ...state.moduleJobs, [moduleId]: jobId },
      })),
      clearModuleJob: (moduleId) => set((state) => {
        const { [moduleId]: _removed, ...rest } = state.moduleJobs; // eslint-disable-line @typescript-eslint/naming-convention
        return { moduleJobs: rest };
      }),
      setWorkingModeApplyJob: (moduleId, version, jobId) => set((state) => ({
        workingModeApplyJobs: {
          ...state.workingModeApplyJobs,
          [workingModeApplyJobKey(moduleId, version)]: jobId,
        },
      })),
      clearWorkingModeApplyJob: (moduleId, version) => set((state) => {
        const key = workingModeApplyJobKey(moduleId, version);
        const { [key]: _removed, ...rest } = state.workingModeApplyJobs; // eslint-disable-line @typescript-eslint/naming-convention
        return { workingModeApplyJobs: rest };
      }),
      setOralPoolJob: (moduleId, version, taskId, jobId) => set((state) => ({
        oralPoolJobs: {
          ...state.oralPoolJobs,
          [oralPoolJobKey(moduleId, version, taskId)]: jobId,
        },
      })),
      clearOralPoolJob: (moduleId, version, taskId) => set((state) => {
        const key = oralPoolJobKey(moduleId, version, taskId);
        const { [key]: _removed, ...rest } = state.oralPoolJobs; // eslint-disable-line @typescript-eslint/naming-convention
        return { oralPoolJobs: rest };
      }),
    }),
    {
      name: 'module-rubric-job-storage',
      storage: createJSONStorage(() => AsyncStorage),
      // Preserve older persisted maps; default missing keys from this session.
      merge: (persisted, current) => {
        const partial = (persisted ?? {}) as Partial<ModuleJobState>;
        return {
          ...current,
          ...partial,
          moduleJobs: partial.moduleJobs ?? current.moduleJobs,
          workingModeApplyJobs: partial.workingModeApplyJobs ?? current.workingModeApplyJobs,
          oralPoolJobs: partial.oralPoolJobs ?? current.oralPoolJobs,
        };
      },
    },
  ),
);
